import { batch, createSignal, createUniqueId, type JSX } from 'solid-js';
import { t } from '../i18n';
import {
  type AdminOssFilter,
  type AdminOssRequest,
  useAdminOssVerifications,
} from '../lib/adminOss';
import { usePagedQuery } from '../lib/paged';
import { AdminOssTable } from './AdminOssTable';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Select } from './Select';
import { Skeleton } from './Skeleton';

// Admin OSS-verification queue container (arc 101 slice 07, #68). A status
// filter over `GET /v1/admin/oss-verifications`, cursor-paginated; the verdict
// happens per row via the `account` MCP verb. Mounts only for staff (behind
// <AdminGate/>). Paging — including the by-`id` reconcile that keeps a
// half-typed reviewer note alive across a refetch — lives in `usePagedQuery`.

const FILTERS: readonly AdminOssFilter[] = ['pending', 'approved', 'rejected', 'all'];

export function AdminOssPanel(props: { token: string | null }): JSX.Element {
  const [status, setStatus] = createSignal<AdminOssFilter>('pending');
  const statusId = createUniqueId();
  const list = usePagedQuery<AdminOssRequest>((cursor) =>
    useAdminOssVerifications(status, cursor, 30, props.token),
  );

  // A filter change and the reset are ONE batched write, so the query key jumps
  // straight to the new filter at `cursor=null` rather than firing a throwaway
  // fetch at the cursor the previous filter had reached.
  const filter = (next: AdminOssFilter): void =>
    batch(() => {
      setStatus(next);
      list.reset();
    });

  // After a verdict, come back on page one and RE-READ it: the decided row
  // leaves the `pending` filter, so refetching a deep cursor would append a
  // shifted slice on top of what's shown — and a plain reset would answer from
  // the 30s cache the verdict just invalidated. That's `reload`.

  return (
    <>
      <PageHeader title={t('admin.oss_title')} desc={t('admin.oss_subtitle')} />

      <div class="filter-row">
        <div class="admin-filter__select">
          <label id={`${statusId}-label`} for={statusId} class="sr-only">
            {t('admin.oss_status_label')}
          </label>
          <Select
            a11y={{ id: statusId }}
            label={t('admin.oss_status_label')}
            value={status()}
            options={FILTERS.map((s) => ({ value: s, label: t(`admin.oss_filter_${s}`) }))}
            onChange={filter}
          />
        </div>
      </div>

      <Panel title={t('admin.oss_queue')}>
        <QueryBoundary
          query={list.view}
          isEmpty={(r) => r.length === 0}
          loading={
            <div class="admin__skeletons">
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
            </div>
          }
          empty={<EmptyState message={t('admin.oss_empty')} />}
        >
          {(r) => (
            <>
              <AdminOssTable rows={r} token={props.token} onDecided={list.reload} />
              <LoadMore list={list} label={t('admin.load_more')} />
            </>
          )}
        </QueryBoundary>
      </Panel>
    </>
  );
}
